import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Compass, Users, Map, Award, Target } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MissionPage = () => {
    // Scroll to top when page loads
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <div className="bg-[#0f172a] text-gray-200 min-h-screen relative overflow-hidden">

            {/* Background Effects */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 -mr-20 w-[800px] h-[800px] bg-purple-500/10 rounded-full blur-[120px] opacity-30" />
                <div className="absolute bottom-0 left-0 -ml-20 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[100px] opacity-30" />
            </div>

            <Navbar />

            <main className="pt-32 pb-20 px-6 relative z-10">
                <div className="container mx-auto">

                    {/* Hero / Title Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-20"
                    >
                        <span className="inline-block py-1 px-3 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-semibold mb-6 tracking-wide">
                            HAKKIMIZDA
                        </span>
                        <h1 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-cyan-200 mb-6 font-heading leading-tight">
                            Geleceği İnşa Eden<br />
                            <span className="text-purple-500">Değerlerimiz</span>
                        </h1>
                    </motion.div>

                    {/* MISYON SECTION */}
                    <div id="misyon" className="mb-24 relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 blur-3xl rounded-full opacity-20 transform scale-y-50" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden shadow-2xl"
                        >
                            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl -mr-32 -mt-32" />

                            <div className="flex flex-col md:flex-row items-center gap-12 relative z-10">
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                                        <Target className="w-8 h-8 text-white" />
                                    </div>
                                </div>
                                <div className="text-center md:text-left">
                                    <h2 className="text-xl md:text-2xl font-bold text-white mb-6 font-heading">
                                        Misyonumuz
                                    </h2>
                                    <p className="text-base md:text-lg text-gray-200 leading-relaxed font-light italic">
                                        "TURET’in misyonu; turist rehberliği mesleğine ilgi duyan öğrencilerin mesleki, kültürel ve kişisel gelişimlerini desteklemek amacıyla eğitimler, seminerler, teknik geziler ve sosyal-kültürel etkinlikler düzenlemek; Türkiye’nin ve dünyanın doğal, tarihi ve kültürel değerlerinin doğru, sürdürülebilir ve etik bir anlayışla tanıtılmasına katkıda bulunmak; akademi, sektör ve öğrenciler arasında güçlü bir iletişim ve iş birliği ortamı oluşturmaktır."
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <FeatureCard
                            icon={<Compass className="w-8 h-8 text-cyan-400" />}
                            title="Kültürel Keşif"
                            desc="Antik kentler ve müzelerde yerinde öğrenim."
                        />
                        <FeatureCard
                            icon={<Users className="w-8 h-8 text-purple-400" />}
                            title="Profesyonel Ağ"
                            desc="Sektör temsilcileri ile tanışma fırsatları."
                        />
                        <FeatureCard
                            icon={<Map className="w-8 h-8 text-emerald-400" />}
                            title="Saha Deneyimi"
                            desc="Gerçek tur simülasyonları ve uygulamalar."
                        />
                        <FeatureCard
                            icon={<Award className="w-8 h-8 text-yellow-400" />}
                            title="Kariyer Odaklı"
                            desc="Mesleki yetkinlik kazandıran sertifikalı eğitimler."
                        />
                    </div>
                </div>
            </main>


        </div>
    );
};

const FeatureCard = ({ icon, title, desc }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -5 }}
        className="p-8 rounded-3xl bg-white/5 border border-white/10 hover:border-purple-500/50 hover:bg-white/[0.07] shadow-lg transition-all group"
    >
        <div className="mb-6 p-4 bg-black/20 rounded-2xl w-fit group-hover:bg-purple-500/20 transition-colors">
            {icon}
        </div>
        <h3 className="text-lg font-bold text-white mb-3">{title}</h3>
        <p className="text-gray-400 text-xs leading-relaxed">
            {desc}
        </p>
    </motion.div>
);

export default MissionPage;
